<template>
  <div class="bg-white rounded">
    <div class="row px-3 pb-3 pl-lg-3 pr-lg-3">
      <div class="col-12">
        <template v-if="editting">
          <SearchReadOnly
            active
            :item="model"
            :academic-year-id="academicYearId"
            :municipality="municipality"
            :select-municipality="selectMunicipality"
            :tap-request-id="tapRequestId"
            @search="search"
            @edit="$emit('edit')"
            @remove="$emit('remove')"
          />
        </template>
        <template v-else>
          <Search
            :academic-year-id="academicYearId"
            :region="region"
            :select-region="selectRegion"
            :exclude-region="excludeRegion"
            :municipality="municipality"
            :select-municipality="selectMunicipality"
            :exclude-municipality="excludeMunicipality"
            :schools-added="schoolsAdded"
            :tap-request-id="tapRequestId"
            @search="search"
            @filterChanged="active = false"
          />
        </template>
      </div>
      <template v-if="showAlternatives">
        <div class="col-12 pt-2 pt-lg-4">
          <CustomSeparator
            text="Resultado de Alternativa de Ubicación"
            class="no-bg-custom-separator"
          />
          <template v-if="alternativesInModel.length">
            <ValidationObserver ref="form">
              <div class="row p-1 p-lg-0">
                <div class="col-12">
                  <ValidationProvider
                    v-slot="{ errors, validate }"
                    mode="lazy"
                    rules="required"
                    :custom-messages="{
                      required:
                        'Favor de aceptar o rechazar al menos una alternativa de ubicación.',
                    }"
                  >
                    <div class="row">
                      <div
                        v-for="(alternative, i) in alternativesInModel"
                        :key="i"
                        class="col-12"
                      >
                        <SchoolAlternativeItem :item="alternative" />
                      </div>
                    </div>

                    <input
                      type="hidden"
                      :value="alternativesSelected"
                      @input="validate"
                    />

                    <CustomError
                      v-if="errors[0]"
                      variant="no-border"
                      :message="errors[0]"
                    />
                  </ValidationProvider>
                </div>
                <div v-if="selectMunicipality" class="col-12 pt-3">
                  <ValidationProvider
                    v-slot="{ errors, validate }"
                    mode="lazy"
                    rules="required"
                    :custom-messages="{
                      required: excludeMunicipality
                        ? 'Favor justificar reomendación de localización fuera del municipio de residencia.'
                        : 'Justificar recomendación de la alternativa de ubicación y/o localización:',
                    }"
                  >
                    <CustomTextArea
                      v-model="model.reason"
                      :label="
                        excludeMunicipality
                          ? 'Justificar recomendación de localización fuera del municipio de residencia:'
                          : 'Justificar recomendación de la alternativa de ubicación y/o localización:'
                      "
                      regular-label
                      :maxlength="3000"
                      show-char-count
                      :count-classes="['d-block text-right']"
                      :error="errors[0]"
                      :custom-error-msg="errors[0]"
                      @input="validate"
                    />
                  </ValidationProvider>
                </div>
              </div>
            </ValidationObserver>
          </template>
          <template v-else>
            <CustomResultNotFound
              text="No se encontraron alternativas disponibles para la escuela en el sistema."
            />
          </template>
        </div>
      </template>
      <div class="col-12 pt-4">
        <div class="d-flex flex-row align-items-center justify-content-end">
          <template v-if="showCancelButton">
            <ActionButton
              text="Cancelar"
              variant="outline-danger"
              i="fas fa-times-circle"
              class="w-100 w-lg-0"
              @click="cancel(true)"
            />
          </template>
          <template v-if="showSubmitButton">
            <div class="mx-1 mx-lg-2" />
            <ActionButton
              :text="editting ? 'Guardar' : 'Añadir'"
              variant="success"
              :i="`fas fa-${editting ? 'save' : 'file-plus'}`"
              class="w-100 w-lg-0"
              @click="submit"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Search from './Search';
import SearchReadOnly from './SearchReadOnly';
import SchoolAlternativeItem from './SchoolAlternativeItem';
import CustomTextArea from 'CustomTextArea';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';
import alert from '@/mixin/alert.mixin';
import { initModelState, initAlternativesModelState } from '../../../_utils';
import { schoolAlternativeDecisionEnum } from '../../../_types';

export default {
  name: 'SchoolRecommendationForm',
  mixins: [alert],
  components: {
    Search,
    SearchReadOnly,
    SchoolAlternativeItem,
    CustomTextArea,
    CustomSeparator,
    CustomResultNotFound,
    ActionButton,
    CustomError,
  },
  props: {
    academicYearId: { type: [Number, String], required: true, default: null },
    region: { type: Object, default: null },
    selectRegion: { type: Boolean, default: false },
    excludeRegion: { type: Boolean, default: false },
    municipality: { type: Object, default: null },
    selectMunicipality: { type: Boolean, default: true },
    excludeMunicipality: { type: Boolean, default: false },
    hasItems: { type: Boolean, default: false },
    schoolsAdded: { type: Array, required: true, default: () => [] },
    /* SchoolRecommendationModel */
    item: { type: Object, default: null },
    editting: { type: Boolean, default: false },
    tapRequestId: { type: [Number, String], required: true, default: null },
  },
  data: () => ({ model: initModelState(), active: false }),
  computed: {
    alternativesInModel() {
      return this.model?.alternatives || [];
    },
    alternativesSelected() {
      return this.alternativesInModel.filter(
        (x) => x.decision !== schoolAlternativeDecisionEnum.None
      );
    },
    hasAlternatives() {
      return !!this.alternativesInModel.length;
    },
    showAlternatives() {
      return this.hasAlternatives || this.active;
    },
    showCancelButton() {
      return this.hasItems || this.editting || this.active;
    },
    showSubmitButton() {
      return this.hasAlternatives && this.showAlternatives;
    },
  },
  methods: {
    search({ filter, result }) {
      if (this.editting) {
        const alternativeAddedIds = this.alternativesInModel.map(
          (x) => x.alternativeId
        );
        const alternativesNotInModel = result
          ?.filter((x) => !alternativeAddedIds.includes(x.alternativeId))
          .map((x) => ({
            ...initAlternativesModelState(),
            ...x,
          }));
        this.model.alternatives = [
          ...this.model.alternatives,
          ...alternativesNotInModel,
        ];
      } else {
        this.model = {
          ...filter,
          alternatives: result.map((x) => ({
            ...initAlternativesModelState(),
            ...x,
          })),
        };
      }
      this.active = true;
    },
    async submit() {
      const formValid = await this.$refs['form'].validate();
      if (!formValid) {
        this.ShowToast(
          'Información Incompleta',
          'Favor de completar la información solicitada para poder continuar.',
          'error'
        );
        return;
      }
      this.$emit('submit', this.model, this.cancel);
    },
    cancel(reInit = false) {
      if (reInit) {
        this.model = this.editting ? this.item : initModelState();
      }
      this.$emit('cancel');
      this.$nextTick(() => {
        this.$refs['form']?.reset();
      });
    },
  },
  created() {
    if (this.item) {
      const nonReactiveItem = JSON.parse(JSON.stringify(this.item));
      this.model = { ...this.model, ...nonReactiveItem };
    }
  },
};
</script>

<style lang="scss" scoped>
.w-lg-0 {
  @media (min-width: 992px) {
    width: inherit !important;
  }
}

::v-deep .no-bg-custom-separator .separator-custom {
  --text-color-active: #727272;
  --background-color-active: #ffffff;
  --line-separator-color: #727272;
  font-weight: 400;
  font-size: 14px;
}
</style>
